import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type TouchEvent } from 'react'
import { Box, ButtonBase, Stack, useMediaQuery } from '@mui/material'
import type { BannerCardapio } from '../api'
import { rastrearClicouBanner } from '../lib/tracking'

const INTERVALO_TROCA_MS = 5000
/** Quanto o dedo anda de lado até contar como arrastar o carrossel, e não como rolar a página. */
const LIMIAR_ARRASTO_PX = 10
/** Sem evento de rolagem por esse tempo, a rolagem que o próprio carrossel pediu terminou. */
const FIM_ROLAGEM_NOSSA_MS = 200

interface BannersCardapioProps {
  banners: BannerCardapio[]
  aoAbrir: (banner: BannerCardapio) => void
}

interface Medida {
  /** Quantos banners cabem inteiros na tela. */
  porTela: number
  /** Onde o carrossel pode parar: com 3 por tela e 5 banners, começando no 1º, no 2º ou no 3º. */
  posicoes: number
  ativa: number
}

function slidesDo(trilho: HTMLElement): HTMLElement[] {
  return Array.from(trilho.children).filter((filho): filho is HTMLElement => filho instanceof HTMLElement)
}

function medirTrilho(trilho: HTMLElement): Medida {
  const slides = slidesDo(trilho)
  const cabem = slides.filter((slide) => slide.offsetLeft + slide.offsetWidth <= trilho.clientWidth + 1).length
  const porTela = Math.min(slides.length, Math.max(1, cabem))
  const posicoes = Math.max(1, slides.length - porTela + 1)
  const rolagemMaxima = Math.max(0, trilho.scrollWidth - trilho.clientWidth)

  let ativa = 0
  let menorDistancia = Number.POSITIVE_INFINITY
  for (let i = 0; i < posicoes; i += 1) {
    const slide = slides[i]
    if (!slide) continue

    // O último banner do celular nunca encosta à esquerda: a parada dele é o fim da rolagem.
    const distancia = Math.abs(Math.min(slide.offsetLeft, rolagemMaxima) - trilho.scrollLeft)
    if (distancia < menorDistancia) {
      menorDistancia = distancia
      ativa = i
    }
  }

  return { porTela, posicoes, ativa }
}

function mesmaMedida(a: Medida, b: Medida): boolean {
  return a.porTela === b.porTela && a.posicoes === b.posicoes && a.ativa === b.ativa
}

function rolarAte(trilho: HTMLElement, indice: number, comportamento: ScrollBehavior): void {
  const slide = slidesDo(trilho)[indice]
  if (!slide) return

  // O jsdom dos testes não tem `scrollTo` em elemento.
  if (typeof trilho.scrollTo === 'function') trilho.scrollTo({ left: slide.offsetLeft, behavior: comportamento })
  else trilho.scrollLeft = slide.offsetLeft
}

function rotuloBolinha(posicao: number, porTela: number, total: number): string {
  if (porTela <= 1) return `Ver banner ${posicao + 1} de ${total}`
  return `Ver banners ${posicao + 1} a ${posicao + porTela} de ${total}`
}

/** Carrossel de banners do topo do cardápio: o próximo espia na borda e a troca é sozinha até o cliente assumir. */
export function BannersCardapio({ banners, aoAbrir }: BannersCardapioProps): JSX.Element | null {
  const trilho = useRef<HTMLDivElement>(null)
  const reduzirMovimento = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [medida, setMedida] = useState<Medida>({ porTela: 1, posicoes: 1, ativa: 0 })
  const [clienteNoControle, setClienteNoControle] = useState(false)
  const [mouseSobre, setMouseSobre] = useState(false)
  const inicioToque = useRef<{ x: number; y: number } | null>(null)
  // Rolagem pedida pela troca automática (ou por girar o aparelho) não é gesto do cliente.
  const rolagemNossa = useRef(false)
  const relogioRolagemNossa = useRef<number | undefined>(undefined)
  const total = banners.length

  function marcarRolagemNossa(): void {
    rolagemNossa.current = true
    window.clearTimeout(relogioRolagemNossa.current)
    relogioRolagemNossa.current = window.setTimeout(() => {
      rolagemNossa.current = false
    }, FIM_ROLAGEM_NOSSA_MS)
  }

  useEffect(() => () => window.clearTimeout(relogioRolagemNossa.current), [])

  useLayoutEffect(() => {
    const elemento = trilho.current
    if (!elemento) return undefined

    const medir = (): void => {
      const nova = medirTrilho(elemento)
      setMedida((atual) => (mesmaMedida(atual, nova) ? atual : nova))
    }

    // Rolagem que não fomos nós que pedimos é o cliente mexendo no carrossel (dedo, trackpad, teclado).
    const aoRolar = (): void => {
      if (rolagemNossa.current) marcarRolagemNossa()
      else setClienteNoControle(true)
      medir()
    }

    medir()
    elemento.addEventListener('scroll', aoRolar, { passive: true })
    window.addEventListener('resize', marcarRolagemNossa)
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir)
    observador?.observe(elemento)

    return () => {
      elemento.removeEventListener('scroll', aoRolar)
      window.removeEventListener('resize', marcarRolagemNossa)
      observador?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total])

  const trocaAutomatica = medida.posicoes > 1 && !clienteNoControle && !mouseSobre && !reduzirMovimento

  useEffect(() => {
    if (!trocaAutomatica) return undefined

    const relogio = window.setInterval(() => {
      const elemento = trilho.current
      if (!elemento) return

      const { posicoes, ativa } = medirTrilho(elemento)
      marcarRolagemNossa()
      rolarAte(elemento, ativa + 1 < posicoes ? ativa + 1 : 0, 'smooth')
    }, INTERVALO_TROCA_MS)

    return () => window.clearInterval(relogio)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trocaAutomatica])

  if (total === 0) return null

  function assumirControle(): void {
    setClienteNoControle(true)
  }

  function aoComecarToque(evento: TouchEvent<HTMLDivElement>): void {
    const toque = evento.touches[0]
    inicioToque.current = toque ? { x: toque.clientX, y: toque.clientY } : null
  }

  // Dedo na vertical é o cliente rolando a página: só arrastar de lado desliga a troca automática.
  function aoMoverToque(evento: TouchEvent<HTMLDivElement>): void {
    const inicio = inicioToque.current
    const toque = evento.touches[0]
    if (!inicio || !toque) return

    const deLado = Math.abs(toque.clientX - inicio.x)
    if (deLado > LIMIAR_ARRASTO_PX && deLado > Math.abs(toque.clientY - inicio.y)) assumirControle()
  }

  // Só o mouse pausa ao passar por cima: no celular o toque também gera "entrar" e nunca "sair".
  function aoEntrarPonteiro(evento: PointerEvent<HTMLElement>): void {
    if (evento.pointerType === 'mouse') setMouseSobre(true)
  }

  function aoSairPonteiro(evento: PointerEvent<HTMLElement>): void {
    if (evento.pointerType === 'mouse') setMouseSobre(false)
  }

  function irPara(posicao: number): void {
    assumirControle()
    if (trilho.current) rolarAte(trilho.current, posicao, reduzirMovimento ? 'auto' : 'smooth')
  }

  function tocar(banner: BannerCardapio, posicao: number): void {
    rastrearClicouBanner({ id: banner.id, nome: banner.nome, posicao })
    aoAbrir(banner)
  }

  const precisaBolinhas = medida.posicoes > 1

  return (
    <Box
      component="section"
      aria-label="Novidades e promoções"
      onPointerEnter={aoEntrarPonteiro}
      onPointerLeave={aoSairPonteiro}
      sx={{ mb: 1 }}
    >
      <Box
        ref={trilho}
        onTouchStart={aoComecarToque}
        onTouchMove={aoMoverToque}
        onFocus={assumirControle}
        sx={{
          // `relative`: o `offsetLeft` de cada banner passa a ser medido a partir do trilho.
          position: 'relative',
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          // Mesmo carrossel dos "Destaques": encaixa cada banner ao parar de arrastar, sem scrollbar.
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {banners.map((banner, indice) => (
          <Box
            key={banner.id}
            sx={(tema) => ({
              flex: '0 0 auto',
              // Celular: 88% deixa o próximo aparecendo na borda (60% com o aparelho deitado). Da tela média para cima cabem 3.
              width: {
                xs: total === 1 ? '100%' : '88%',
                sm: '60%',
                md: `calc((100% - ${tema.spacing(3)}) / 3)`,
              },
              scrollSnapAlign: 'start',
            })}
          >
            <SlideBanner banner={banner} posicao={indice + 1} total={total} aoTocar={tocar} />
          </Box>
        ))}
      </Box>

      {total > 1 && (
        // A linha ocupa o lugar mesmo quando não precisa: aparecer depois muda a altura e o "rolar até o grupo" erra o ponto.
        <Stack
          direction="row"
          justifyContent="center"
          aria-hidden={precisaBolinhas ? undefined : true}
          sx={(tema) => ({ height: tema.spacing(4), visibility: precisaBolinhas ? 'visible' : 'hidden' })}
        >
          {Array.from({ length: medida.posicoes }, (_, posicao) => (
            <ButtonBase
              key={posicao}
              focusRipple
              onClick={() => irPara(posicao)}
              aria-label={rotuloBolinha(posicao, medida.porTela, total)}
              aria-current={posicao === medida.ativa ? 'true' : undefined}
              // A bolinha é pequena; o respiro em volta é o que dá 32×32 de área de toque.
              sx={{ p: 1.5, borderRadius: 4 }}
            >
              <Box
                sx={(tema) => ({
                  width: posicao === medida.ativa ? 16 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: posicao === medida.ativa ? 'primary.main' : 'action.disabled',
                  transition: tema.transitions.create(['width', 'background-color']),
                })}
              />
            </ButtonBase>
          ))}
        </Stack>
      )}
    </Box>
  )
}

function SlideBanner({
  banner,
  posicao,
  total,
  aoTocar,
}: {
  banner: BannerCardapio
  posicao: number
  total: number
  aoTocar: (banner: BannerCardapio, posicao: number) => void
}): JSX.Element {
  const imagem = (
    <Box
      component="img"
      src={banner.urlImagem}
      alt={banner.nome?.trim() || `Banner ${posicao} de ${total}`}
      // Os dois primeiros aparecem na abertura; os demais só quando o carrossel chega perto.
      loading={posicao >= 3 ? 'lazy' : undefined}
      draggable={false}
      sx={{
        display: 'block',
        width: '100%',
        // Quadro 2:1 fixo: a arte no formato pedido no admin nunca é cortada.
        aspectRatio: '2 / 1',
        objectFit: 'cover',
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    />
  )

  // Aviso (sem link) é só a imagem: nada de efeito de toque prometendo uma ação que não existe.
  if (banner.destino.tipo === 'NENHUM') return imagem

  return (
    <ButtonBase
      focusRipple
      onClick={() => aoTocar(banner, posicao)}
      sx={{ display: 'block', width: '100%', borderRadius: 2, overflow: 'hidden' }}
    >
      {imagem}
    </ButtonBase>
  )
}
